/**
 * Platform HQ — Frontend error log triage.
 *
 * Cross-tenant view of every client-side error captured by HinesTime tenants.
 * Operators can filter, mark resolved, bulk-purge, and "Copy for AI" — bundle
 * up to 10 selected errors into a clean markdown block to paste straight into
 * Claude/ChatGPT/Cursor.
 *
 * Moved here from the tenant sidebar (Feb 2026) so tenants aren't distracted
 * by raw JS errors. Errors still flow silently via POST /api/client-errors.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { usePlatformAuth } from '../context/PlatformAuthContext';
import {
  ArrowLeft, Search, Loader2, AlertOctagon, CheckCircle2, RotateCcw, Copy, Check,
  Trash2, AlertTriangle, Info, LogOut, Bug,
} from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;
const sevColor = (s) => ({ error: 'bg-rose-700/40 text-rose-200 border-rose-700/60', warn: 'bg-amber-700/40 text-amber-200 border-amber-700/60', info: 'bg-blue-700/40 text-blue-200 border-blue-700/60' }[s] || 'bg-slate-700/40 text-slate-200');

function shortDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }); } catch { return iso; }
}
function truncate(s, n) { return !s ? '' : (s.length > n ? s.slice(0, n) + '…' : s); }

export default function PlatformErrorLog() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { admin, token, logout } = usePlatformAuth();

  // Redirect to signin if not authed
  useEffect(() => { if (!admin) navigate('/platform-admin/signin'); }, [admin, navigate]);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // Filters
  const [severity, setSeverity] = useState('');           // '' | 'error' | 'warn' | 'info'
  const [resolved, setResolved] = useState('open');       // 'open' | 'resolved' | 'all'
  const [companyId, setCompanyId] = useState('');         // '' = all
  const [routeFilter, setRouteFilter] = useState('');      // '' = all pages
  const [sinceDays, setSinceDays] = useState(7);
  const [search, setSearch] = useState('');

  // Selection (for "Copy for AI" + bulk actions)
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Expanded row detail view (stack visible)
  const [expandedId, setExpandedId] = useState(null);

  // Bulk delete dialog
  const [bulkBusy, setBulkBusy] = useState(false);
  // Copy-for-AI dialog
  const [aiMarkdown, setAiMarkdown] = useState(null);
  const [copied, setCopied] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Companies dropdown — derived from current results so we don't make an
  // extra round-trip. Good enough for the operator's typical view of last
  // 7-30 days.
  const companiesOnPage = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      if (it.company_id) map.set(it.company_id, it.company_name || it.company_id);
    }
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [items]);

  const reload = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = {};
      if (severity) params.severity = severity;
      if (resolved === 'open') params.resolved = false;
      else if (resolved === 'resolved') params.resolved = true;
      if (companyId) params.company_id = companyId;
      if (routeFilter) params.route = routeFilter;
      if (sinceDays) params.since_days = sinceDays;
      if (search.trim()) params.search = search.trim();
      params.limit = 100;
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API}/platform/errors`, { headers, params }),
        axios.get(`${API}/platform/errors/stats`, { headers }),
      ]);
      setItems(listRes.data?.items || []);
      setTotal(listRes.data?.total || 0);
      setStats(statsRes.data || null);
      setSelectedIds(new Set());
    } catch (err) {
      toast({ title: 'Could not load errors', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [token, severity, resolved, companyId, routeFilter, sinceDays, search, headers, toast]);

  useEffect(() => { reload(); }, [reload]);

  if (!admin) return null;

  const toggleSelect = (id) => setSelectedIds(s => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const resolveOne = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/platform/errors/${id}/resolve`, {}, { headers });
      await reload();
    } catch (err) {
      toast({ title: 'Could not resolve', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const reopenOne = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/platform/errors/${id}/reopen`, {}, { headers });
      await reload();
    } catch (err) {
      toast({ title: 'Could not reopen', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const deleteOne = async (id) => {
    if (!window.confirm('Permanently delete this error report?')) return;
    setBusyId(id);
    try {
      await axios.delete(`${API}/platform/errors/${id}`, { headers });
      await reload();
    } catch (err) {
      toast({ title: 'Could not delete', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const bulkPurge = async () => {
    const days = parseInt(window.prompt('Delete RESOLVED errors older than how many days? (default 7)', '7') || '0', 10);
    if (!days || days < 1) return;
    if (!window.confirm(`Permanently delete all RESOLVED errors older than ${days} days?`)) return;
    setBulkBusy(true);
    try {
      const r = await axios.delete(`${API}/platform/errors`, { headers, params: { older_than_days: days, resolved_only: true } });
      toast({ title: 'Purged', description: `Deleted ${r.data?.deleted || 0} resolved errors older than ${days} days` });
      await reload();
    } catch (err) {
      toast({ title: 'Purge failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setBulkBusy(false);
    }
  };

  const copyForAI = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) {
      toast({ title: 'Select at least one error', variant: 'destructive' });
      return;
    }
    if (ids.length > 10) {
      toast({ title: 'Max 10 errors per export', description: 'Uncheck some to fit AI context window', variant: 'destructive' });
      return;
    }
    try {
      const r = await axios.post(`${API}/platform/errors/export`, { error_ids: ids }, { headers });
      setAiMarkdown(r.data?.markdown || '');
      setCopied(false);
    } catch (err) {
      toast({ title: 'Export failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(aiMarkdown || '');
      setCopied(true);
      toast({ title: 'Copied — paste into Claude/ChatGPT/Cursor' });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast({ title: 'Clipboard blocked', description: 'Select the text manually and copy', variant: 'destructive' });
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#0A0F1A', color: '#E2E8F0' }}>
      {/* Header */}
      <div className="border-b" style={{ background: '#0F1923', borderColor: '#2D3748' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/platform-admin/dashboard')} className="text-slate-300" data-testid="hq-errors-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
            </Button>
            <div className="flex items-center gap-2">
              <Bug className="w-5 h-5 text-rose-400" />
              <h1 className="text-lg font-bold text-white">Client Error Log</h1>
              <Badge className="bg-rose-700 text-white text-xs">Platform HQ</Badge>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400 hidden sm:inline">{admin?.email}</span>
            <Button variant="ghost" size="sm" onClick={logout} className="text-slate-300" data-testid="hq-errors-logout">
              <LogOut className="w-4 h-4 mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {/* Stats tiles (last 7 days) */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="hq-errors-stats">
            <Card className="bg-[#0F1923] border-[#2D3748]">
              <CardContent className="p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Unresolved (all-time)</div>
                <div className="text-2xl font-bold text-white">{stats.total_unresolved}</div>
              </CardContent>
            </Card>
            <Card className="bg-[#0F1923] border-[#2D3748]">
              <CardContent className="p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Last 7 days</div>
                <div className="text-2xl font-bold text-white">{stats.total_last_7d}</div>
              </CardContent>
            </Card>
            <Card className="bg-[#0F1923] border-[#2D3748]">
              <CardContent className="p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Errors / Warns / Info (7d)</div>
                <div className="text-sm font-medium text-white">
                  <span className="text-rose-400">{stats.by_severity?.error || 0}</span> /
                  <span className="text-amber-400 ml-1">{stats.by_severity?.warn || 0}</span> /
                  <span className="text-blue-400 ml-1">{stats.by_severity?.info || 0}</span>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-[#0F1923] border-[#2D3748]">
              <CardContent className="p-3">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Top tenant (7d)</div>
                <div className="text-sm font-medium text-white truncate" title={stats.top_companies?.[0]?.company_name || '—'}>
                  {stats.top_companies?.[0]?.company_name || '—'}
                  {stats.top_companies?.[0]?.count != null && (
                    <span className="text-slate-400 text-xs ml-1">({stats.top_companies[0].count})</span>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Top pages (7d) — grouped-by-page view (Aug 2026) */}
        {stats?.top_routes?.length > 0 && (
          <div className="rounded-lg border border-[#2D3748] bg-[#0F1923] p-3" data-testid="hq-errors-top-pages">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">
              Top pages by error volume (7d) — click to filter
            </div>
            <div className="flex flex-wrap gap-2">
              {stats.top_routes.map((r) => (
                <button
                  key={r.route}
                  onClick={() => setRouteFilter(routeFilter === r.route ? '' : r.route)}
                  data-testid={`hq-route-chip-${r.route.replace(/\//g, '_')}`}
                  className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                    routeFilter === r.route
                      ? 'bg-rose-900/60 border-rose-700 text-rose-100'
                      : 'bg-[#1A2433] border-[#2D3748] text-slate-300 hover:border-slate-500'
                  }`}
                >
                  {r.route} <span className="text-slate-400 ml-1">({r.count})</span>
                </button>
              ))}
              {routeFilter && (
                <button
                  onClick={() => setRouteFilter('')}
                  data-testid="hq-route-filter-clear"
                  className="px-2.5 py-1 rounded-full text-xs border border-slate-600 text-slate-400 hover:text-white"
                >
                  Clear page filter ✕
                </button>
              )}
            </div>
          </div>
        )}

        {/* Filters bar */}
        <div className="rounded-lg border border-[#2D3748] bg-[#0F1923] p-3 grid grid-cols-1 sm:grid-cols-6 gap-2" data-testid="hq-errors-filters">
          <select value={severity} onChange={(e) => setSeverity(e.target.value)} className="rounded-md bg-[#1A2433] border border-[#2D3748] text-white px-2 py-2 text-sm" data-testid="hq-filter-severity">
            <option value="">All severities</option>
            <option value="error">Errors only</option>
            <option value="warn">Warnings</option>
            <option value="info">Info</option>
          </select>
          <select value={resolved} onChange={(e) => setResolved(e.target.value)} className="rounded-md bg-[#1A2433] border border-[#2D3748] text-white px-2 py-2 text-sm" data-testid="hq-filter-resolved">
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
            <option value="all">All</option>
          </select>
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)} className="rounded-md bg-[#1A2433] border border-[#2D3748] text-white px-2 py-2 text-sm" data-testid="hq-filter-company">
            <option value="">All tenants</option>
            {companiesOnPage.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={sinceDays} onChange={(e) => setSinceDays(parseInt(e.target.value, 10))} className="rounded-md bg-[#1A2433] border border-[#2D3748] text-white px-2 py-2 text-sm" data-testid="hq-filter-since">
            <option value="1">Last 24h</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search message/url…"
            className="bg-[#1A2433] border-[#2D3748] text-white text-sm sm:col-span-2"
            data-testid="hq-filter-search"
          />
        </div>

        {/* Action bar */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs text-slate-400">
            {loading ? 'Loading…' : `Showing ${items.length} of ${total} matching errors`}
            {selectedIds.size > 0 && <span className="ml-2 text-emerald-300">· {selectedIds.size} selected</span>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={copyForAI}
              disabled={selectedIds.size === 0}
              className="border-emerald-700 text-emerald-300 hover:bg-emerald-700 hover:text-white disabled:opacity-50"
              data-testid="hq-errors-copy-ai-btn"
            >
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy for AI ({selectedIds.size}/10)
            </Button>
            <Button size="sm" variant="ghost" onClick={reload} className="text-slate-300" data-testid="hq-errors-refresh">
              <Search className="w-3.5 h-3.5 mr-1" /> Refresh
            </Button>
            <Button size="sm" variant="ghost" onClick={bulkPurge} disabled={bulkBusy} className="text-rose-400 hover:text-rose-300" data-testid="hq-errors-bulk-purge">
              {bulkBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
              Bulk purge resolved
            </Button>
          </div>
        </div>

        {/* List */}
        <div className="space-y-1.5" data-testid="hq-errors-list">
          {loading ? (
            <div className="py-12 text-center text-slate-500"><Loader2 className="w-5 h-5 inline mr-2 animate-spin" />Loading…</div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-slate-500 border border-dashed border-[#2D3748] rounded-md">
              <CheckCircle2 className="w-8 h-8 inline mb-2 text-emerald-500" />
              <div className="text-sm">No errors match the current filters. Quiet day.</div>
            </div>
          ) : items.map((e) => {
            const isExpanded = expandedId === e.id;
            const isSelected = selectedIds.has(e.id);
            return (
              <div
                key={e.id}
                className={`rounded-md border p-3 transition-colors ${
                  e.resolved ? 'bg-emerald-950/15 border-emerald-800/40' : 'bg-[#0F1923] border-[#2D3748]'
                } ${isSelected ? 'ring-1 ring-emerald-500' : ''}`}
                data-testid={`hq-error-row-${e.id}`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelect(e.id)}
                    className="mt-1 accent-emerald-500"
                    data-testid={`hq-error-select-${e.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`text-[10px] uppercase border ${sevColor(e.severity)}`}>{e.severity || 'error'}</Badge>
                      {e.resolved ? (
                        <Badge className="bg-emerald-700/40 text-emerald-200 border border-emerald-700/60 text-[10px]">Resolved</Badge>
                      ) : (
                        <Badge className="bg-rose-700/40 text-rose-200 border border-rose-700/60 text-[10px]">Open</Badge>
                      )}
                      <span className="text-xs text-slate-400">{shortDate(e.server_ts)}</span>
                      <span className="text-xs text-slate-300 font-medium truncate">
                        {e.company_name || <span className="text-slate-500 italic">(anon)</span>}
                      </span>
                      <span className="text-xs text-slate-500 truncate">· {e.route || '/'}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setExpandedId(isExpanded ? null : e.id)}
                      className="mt-1 text-sm text-white text-left font-mono w-full truncate hover:text-emerald-300"
                      data-testid={`hq-error-toggle-${e.id}`}
                      title={isExpanded ? 'Collapse' : 'Show full stack'}
                    >
                      {truncate(e.message, 200)}
                    </button>
                    {isExpanded && (
                      <div className="mt-2 space-y-2 text-xs">
                        <div className="text-slate-400">
                          <strong>URL:</strong> <span className="text-slate-300 break-all">{e.url || '(no url)'}</span>
                        </div>
                        <div className="text-slate-400">
                          <strong>UA:</strong> <span className="text-slate-300 break-all">{truncate(e.user_agent, 220)}</span>
                        </div>
                        {e.stack && (
                          <div>
                            <strong className="text-slate-400">Stack:</strong>
                            <pre className="mt-1 p-2 rounded bg-[#0B131F] border border-[#2D3748] text-slate-300 overflow-x-auto text-[10px] leading-snug">{e.stack}</pre>
                          </div>
                        )}
                        {e.extra && Object.keys(e.extra).length > 0 && (
                          <div>
                            <strong className="text-slate-400">Extra:</strong>
                            <pre className="mt-1 p-2 rounded bg-[#0B131F] border border-[#2D3748] text-slate-300 overflow-x-auto text-[10px]">{JSON.stringify(e.extra, null, 2)}</pre>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    {e.resolved ? (
                      <button type="button" onClick={() => reopenOne(e.id)} disabled={busyId === e.id} className="p-1 text-amber-300 hover:text-amber-200" title="Reopen" data-testid={`hq-error-reopen-${e.id}`}>
                        {busyId === e.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                      </button>
                    ) : (
                      <button type="button" onClick={() => resolveOne(e.id)} disabled={busyId === e.id} className="p-1 text-emerald-300 hover:text-emerald-200" title="Mark resolved" data-testid={`hq-error-resolve-${e.id}`}>
                        {busyId === e.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                      </button>
                    )}
                    <button type="button" onClick={() => deleteOne(e.id)} disabled={busyId === e.id} className="p-1 text-rose-400 hover:text-rose-300" title="Delete" data-testid={`hq-error-delete-${e.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Copy-for-AI modal */}
      {aiMarkdown && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50" data-testid="hq-errors-ai-modal">
          <div className="bg-[#0F1923] border border-emerald-700 rounded-lg w-full max-w-3xl max-h-[85vh] flex flex-col">
            <div className="p-4 border-b border-emerald-800/40 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Copy className="w-5 h-5 text-emerald-400" />
                <h2 className="text-base font-semibold text-white">Markdown bundle for AI</h2>
              </div>
              <button type="button" onClick={() => setAiMarkdown(null)} className="text-slate-400 hover:text-white">✕</button>
            </div>
            <textarea
              value={aiMarkdown}
              readOnly
              className="flex-1 m-4 p-3 bg-[#0B131F] border border-[#2D3748] rounded text-slate-200 font-mono text-xs resize-none overflow-auto"
              style={{ minHeight: 300 }}
              onClick={(e) => e.target.select()}
              data-testid="hq-errors-ai-textarea"
            />
            <div className="p-4 border-t border-[#2D3748] flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setAiMarkdown(null)} className="text-slate-300">Close</Button>
              <Button onClick={copyToClipboard} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="hq-errors-ai-copy">
                {copied ? <><Check className="w-4 h-4 mr-1.5" /> Copied!</> : <><Copy className="w-4 h-4 mr-1.5" /> Copy to clipboard</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
